/**
 * DisplaySettings.tsx
 * Split-view widget display settings with live preview
 * LEFT: Settings panel | RIGHT: Live preview
 * Path: components/admin-tabs/reviews/DisplaySettings.tsx
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { UserProfile, WidgetSettings, Review } from '../../../types';
import { Save, Grid3x3, Loader, Eye, Layers, X } from 'lucide-react';
import { 
  GridSettings, 
  SnippetsSettings
} from './display-settings';
import { GridPreview } from './display-settings/previews/GridPreview';
import { SnippetsPreview } from './display-settings/previews/SnippetsPreview';
import { ManualProductGrouping } from './ManualProductGrouping';

interface DisplaySettingsProps {
  user: UserProfile;
}

export const DisplaySettings: React.FC<DisplaySettingsProps> = ({ user }) => {
  const [settings, setSettings] = useState<Partial<WidgetSettings>>({
    layout: 'grid',
    reviews_per_page: 6,
    star_color: '#fbbf24',
    card_style: 'shadow',
    show_verified_badge: true,
    show_response: true,
    show_media: true,
    min_rating_display: 0,
    sort_order: 'recent',
    group_by_shopify_tags: false,
  });
  
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showManualGrouping, setShowManualGrouping] = useState(false);

  useEffect(() => {
    loadSettings();
    loadPreviewReviews();
  }, [user.id]);

  const loadSettings = async () => {
    try {
      const data = await api.getWidgetSettings(user.id);
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadPreviewReviews = async () => {
    try {
      setLoadingReviews(true);
      // Fetch only approved reviews for preview
      const data = await api.getReviewsForAdmin(user.id, { status: 'approved' });
      
      // Apply widget settings filters
      let filteredReviews = data;
      
      if (settings.min_rating_display) {
        filteredReviews = filteredReviews.filter(r => r.rating >= (settings.min_rating_display || 0));
      }
      
      // Apply sorting
      if (settings.sort_order === 'rating_high') {
        filteredReviews.sort((a, b) => b.rating - a.rating);
      } else if (settings.sort_order === 'rating_low') {
        filteredReviews.sort((a, b) => a.rating - b.rating);
      } else {
        // Most recent (default)
        filteredReviews.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
      
      // Limit to reasonable number for preview
      setReviews(filteredReviews.slice(0, 12));
    } catch (error) {
      console.error('Failed to load preview reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateWidgetSettings(user.id, settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = (updates: Partial<WidgetSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex gap-6 min-h-[calc(100vh-200px)] relative">
      
      {/* ===== LEFT PANEL: SETTINGS (40%) ===== */}
      <div className="w-2/5 space-y-6 flex-shrink-0">
        
        {/* Layout Selection */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout Style</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleUpdate({ layout: 'grid' })}
              className={`p-4 border-2 rounded-lg transition-all ${
                settings.layout === 'grid'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Grid3x3 className={`w-6 h-6 mx-auto mb-2 ${
                settings.layout === 'grid' ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <span className={`block text-xs font-medium ${
                settings.layout === 'grid' ? 'text-blue-700' : 'text-gray-700'
              }`}>
                Grid
              </span>
            </button>

            <button
              onClick={() => handleUpdate({ layout: 'snippets' })}
              className={`p-4 border-2 rounded-lg transition-all ${
                settings.layout === 'snippets'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <svg 
                className={`w-6 h-6 mx-auto mb-2 ${
                  settings.layout === 'snippets' ? 'text-blue-600' : 'text-gray-400'
                }`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className={`block text-xs font-medium ${
                settings.layout === 'snippets' ? 'text-blue-700' : 'text-gray-700'
              }`}>
                Snippets
              </span>
            </button>
          </div>
        </div>

        {/* Layout-Specific Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {settings.layout === 'grid' && '⚙️ Grid Settings'}
            {settings.layout === 'snippets' && '⚙️ Snippets Settings'}
          </h3>
          
          {settings.layout === 'grid' && (
            <GridSettings settings={settings} onUpdate={handleUpdate} />
          )}
          
          {settings.layout === 'snippets' && (
            <SnippetsSettings settings={settings} onUpdate={handleUpdate} />
          )}
        </div>

        {/* Common Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Settings</h3>
          <div className="space-y-4">
            
            {/* Star Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Star Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={settings.star_color || '#fbbf24'}
                  onChange={(e) => handleUpdate({ star_color: e.target.value })}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.star_color || '#fbbf24'}
                  onChange={(e) => handleUpdate({ star_color: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
                />
              </div>
            </div>

            {/* Minimum Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Rating to Display
              </label>
              <select
                value={settings.min_rating_display || 0}
                onChange={(e) => handleUpdate({ min_rating_display: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value={0}>All Ratings</option>
                <option value={1}>1★ and above</option>
                <option value={2}>2★ and above</option>
                <option value={3}>3★ and above</option>
                <option value={4}>4★ and above</option>
                <option value={5}>5★ only</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort Order
              </label>
              <select
                value={settings.sort_order || 'recent'}
                onChange={(e) => handleUpdate({ sort_order: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="recent">Most Recent</option>
                <option value="rating_high">Highest Rating</option>
                <option value="rating_low">Lowest Rating</option>
                <option value="helpful">Most Helpful</option>
              </select>
            </div>

            {/* 🆕 Product Grouping Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-500" />
                Product Grouping
              </h4>
              
              {/* Manual Grouping Controls */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
                <h5 className="text-sm font-medium text-gray-900 mb-1">Manual Grouping</h5>
                <p className="text-xs text-gray-500 mb-3">
                  Manually select products to share reviews. Useful for distinct products that are related.
                </p>
                <button
                  onClick={() => setShowManualGrouping(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Layers className="w-4 h-4" />
                  Manage Product Groups
                </button>
              </div>

              {/* Tag-based Grouping Toggle */}
              <label className="flex items-start gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={settings.group_by_shopify_tags ?? false}
                  onChange={(e) => handleUpdate({ 
                    group_by_shopify_tags: e.target.checked 
                  })}
                  className="w-5 h-5 text-blue-600 rounded mt-0.5 focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700 block">
                    Automatic Tag Grouping
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically group products by Shopify tags. Perfect for variants (e.g., color, size).
                  </p>
                </div>
              </label>
              
              {/* Tag Grouping Settings (shown when enabled) */}
              {settings.group_by_shopify_tags && (
                <div className="ml-8 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  
                  {/* Tag Selection Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Which tag to use?
                    </label>
                    <select
                      value={settings.grouping_tag_key || 'first'}
                      onChange={(e) => handleUpdate({ 
                        grouping_tag_key: e.target.value as 'first' | 'product_type' | 'category'
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="first">First tag in list</option>
                      <option value="product_type">Product Type field</option>
                      <option value="category">Category tag (if exists)</option>
                    </select>
                  </div>

                  {/* Max Reviews Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum reviews to show
                    </label>
                    <input
                      type="number"
                      value={settings.max_grouped_reviews || 300}
                      onChange={(e) => handleUpdate({ 
                        max_grouped_reviews: parseInt(e.target.value) 
                      })}
                      className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      min="10"
                      max="500"
                      step="10"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 bg-white border border-gray-200 rounded-lg p-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* ===== RIGHT PANEL: LIVE PREVIEW (60%) ===== */}
      <div className="w-3/5 flex-shrink-0">
        <div className="sticky top-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            
            {/* Preview Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Changes update instantly as you adjust settings
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Layout</div>
                  <div className="text-sm font-semibold text-gray-900 capitalize">
                    {settings.layout}
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Content */}
            <div className="p-6 bg-gray-50 max-h-[calc(100vh-300px)] overflow-y-auto">
              {loadingReviews ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                  <p className="text-sm text-gray-600">Loading preview...</p>
                </div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">💬</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No Reviews Yet</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Approve some reviews in the Moderation tab to see them here
                  </p>
                  <button
                    onClick={loadPreviewReviews}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Refresh Preview
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  {settings.layout === 'grid' && (
                    <GridPreview reviews={reviews} settings={settings} />
                  )}
                  
                  {settings.layout === 'snippets' && (
                    <SnippetsPreview reviews={reviews} settings={settings} />
                  )}
                </div>
              )}
            </div>

            {/* Preview Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  💡 Showing {reviews.length} approved review{reviews.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={loadPreviewReviews}
                  className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MANUAL GROUPING MODAL ===== */}
      {showManualGrouping && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowManualGrouping(false)}
          />
          
          {/* Modal Panel */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-900">
                  Manual Product Grouping
                </h3>
                <button
                  onClick={() => setShowManualGrouping(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 max-h-[80vh] overflow-y-auto">
                <ManualProductGrouping user={user} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
